import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  List, Card, Progress, Button, Space, Tag, Typography, Alert,
  Empty, Spin, Tooltip, Badge, Divider, message, Popconfirm, Drawer
} from 'antd';
import {
  UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, 
  PauseCircleOutlined, DeleteOutlined, ReloadOutlined,
  CloudUploadOutlined, FileOutlined, ClockCircleOutlined, CloseOutlined
} from '@ant-design/icons';
import axios from '../../api';

// 防抖函数
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 独立的进度组件，避免整个列表重新渲染 - 优化版本
const TaskProgress = React.memo(({ task, onProgressUpdate }) => {
  // 直接使用task的进度数据，确保实时性
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isActive = task.status === 'uploading' || task.status === 'pending';
  const isCompleted = task.status === 'success';

  if (!isActive && !isCompleted) {
    return null;
  }

  // 确保进度值在有效范围内
  // 如果进度达到100%但状态还是uploading，说明还在处理中，显示99%
  const progressPercent = isCompleted ? 100 : 
    (task.progress >= 100 && task.status === 'uploading') ? 99 : 
    Math.min(Math.max(task.progress || 0, 0), 100);
  
  // 已上传字节数：如果状态是uploading且进度100%，显示文件大小-1字节，表示还在处理中
  const uploadedBytes = isCompleted ? task.file_size : 
    (task.progress >= 100 && task.status === 'uploading') ? Math.max(0, task.file_size - 1) :
    (task.uploaded_bytes || 0);
  
  // 只有当状态真正为success且进度为100%时才显示完成
  const isReallyCompleted = isCompleted && task.progress === 100;
  
  // 判断是否正在处理中（进度100%但状态还是uploading）
  const isProcessing = task.status === 'uploading' && task.progress >= 95;

  return (
    <div style={{ marginBottom: '6px' }}>
      <Progress
        percent={progressPercent}
        size="small"
        status={isReallyCompleted ? 'success' : 
          (isProcessing ? 'active' : 
          (task.status === 'uploading' ? 'active' : 'normal'))}
        format={(percent) => `${percent}%`}
        style={{ 
          fontSize: '11px',
          transition: 'all 0.2s ease-in-out' // 减少过渡时间，提高响应速度
        }}
        strokeColor={isReallyCompleted ? '#52c41a' : 
          (isProcessing ? '#faad14' : // 黄色表示处理中
          {
            '0%': '#108ee9',
            '100%': '#87d068',
          })}
      />
      <div style={{ 
        fontSize: '11px', 
        color: '#666', 
        marginTop: '2px',
        transition: 'all 0.2s ease-in-out' // 减少过渡时间，提高响应速度
      }}>
        {formatFileSize(uploadedBytes)} / {formatFileSize(task.file_size)}
      </div>
    </div>
  );
});

const { Text, Title } = Typography;

const UploadTaskList = ({ visible, onClose, onTaskComplete }) => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const progressCache = useRef(new Map()); // 缓存进度数据
  const lastUpdateTime = useRef(0); // 记录上次更新时间
  const hasActiveTasksRef = useRef(false); // 记录是否有活动任务

  // 获取上传任务列表 - 优化版本，减少不必要的刷新
  const fetchTasks = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setLoading(true);
      }
      
      const response = await axios.get('/api/upload-tasks/');
      const newTasks = response.data.results || response.data || [];
      
      // 保留最近成功的任务（30秒内），其他成功的任务和已删除的任务会被过滤掉
      const now = Date.now();
      const filteredTasks = newTasks.filter(task => {
        if (task.status === 'deleted' || !task.id) {
          return false;
        }
        
        // 如果是成功的任务，检查是否在30秒内完成
        if (task.status === 'success') {
          const completedTime = new Date(task.completed_at || task.updated_at).getTime();
          return (now - completedTime) < 30000; // 30秒内
        }
        
        return true;
      });
      
      // 增量更新：只更新真正变化的任务
      setTasks(prevTasks => {
        // 如果任务数量不同，直接更新
        if (prevTasks.length !== filteredTasks.length) {
          return filteredTasks;
        }
        
        // 检查是否有任务状态或错误信息发生变化
        const hasChanges = filteredTasks.some((newTask, index) => {
          const oldTask = prevTasks[index];
          if (!oldTask) return true;
          
          // 只检查状态和错误信息变化，不检查进度变化（进度由专门的进度更新处理）
          return (
            newTask.status !== oldTask.status ||
            newTask.error_message !== oldTask.error_message ||
            newTask.completed_at !== oldTask.completed_at
          );
        });
        
        return hasChanges ? filteredTasks : prevTasks;
      });
      
      // 如果有成功的任务被过滤掉，通知父组件
      // 只有当任务状态为success且进度为100%时才认为是真正完成
      const completedTasks = newTasks.filter(task => 
        task.status === 'success' && task.progress === 100
      );
      if (completedTasks.length > 0 && onTaskComplete) {
        onTaskComplete(completedTasks);
      }
      
      // 检查是否还有活动任务，如果没有则停止定时器
      const hasActiveTasks = filteredTasks.some(task => 
        task.status === 'uploading' || task.status === 'pending'
      );
      
      // 更新活动任务状态
      hasActiveTasksRef.current = hasActiveTasks;
      lastUpdateTime.current = Date.now();
      
      if (!hasActiveTasks && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
    } catch (error) {
      console.error('获取上传任务失败:', error);
      if (forceRefresh) {
        message.error(t('common.getUploadTasksFailed'));
      }
    } finally {
      if (forceRefresh) {
        setLoading(false);
      }
    }
  };

  // 防抖版本的fetchTasks
  const debouncedFetchTasks = useCallback(
    debounce(fetchTasks, 500), // 500ms防抖
    []
  );

  // 增量更新单个任务
  const updateTaskProgress = useCallback((taskId, updates) => {
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      )
    );
  }, []);

  // 精细进度更新：只更新进度，不触发整个列表重新渲染
  const updateTaskProgressOnly = useCallback((taskId, progress, uploadedBytes) => {
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId 
          ? { 
              ...task, 
              progress: progress,
              uploaded_bytes: uploadedBytes || task.uploaded_bytes
            } 
          : task
      )
    );
  }, []);

  // 独立的进度更新机制 - 纯增量更新版本，优化实时性
  const updateProgressOnly = useCallback(async () => {
    try {
      const response = await axios.get('/api/upload-tasks/');
      const newTasks = response.data.results || response.data || [];
      
      // 使用函数式更新来避免依赖tasks
      setTasks(prevTasks => {
        // 检查是否有任务被删除
        const currentTaskIds = new Set(prevTasks.map(task => task.id));
        const newTaskIds = new Set(newTasks.map(task => task.id));
        
        // 移除已删除的任务
        const deletedTaskIds = Array.from(currentTaskIds).filter(id => !newTaskIds.has(id));
        if (deletedTaskIds.length > 0) {
          // 清理已删除任务的缓存
          deletedTaskIds.forEach(id => progressCache.current.delete(id));
          return prevTasks.filter(task => !deletedTaskIds.includes(task.id));
        }
        
        // 纯增量更新：只更新进度相关的字段，避免整个任务对象重新创建
        const updatedTasks = prevTasks.map(task => {
          const newTask = newTasks.find(nt => nt.id === task.id);
          if (newTask && (newTask.status === 'uploading' || newTask.status === 'pending')) {
            // 检查进度是否真的有变化，避免不必要的更新
            const progressChanged = (
              newTask.progress !== task.progress ||
              newTask.uploaded_bytes !== task.uploaded_bytes ||
              newTask.recent_upload_speed !== task.recent_upload_speed
            );
            
            if (progressChanged) {
              // 记录进度变化时间，用于智能更新
              lastUpdateTime.current = Date.now();
              
              return {
                ...task,
                progress: newTask.progress,
                uploaded_bytes: newTask.uploaded_bytes,
                recent_upload_speed: newTask.recent_upload_speed || task.recent_upload_speed
              };
            }
          }
          return task; // 没有变化，返回原对象
        });
        
        return updatedTasks;
      });
    } catch (error) {
      console.error('更新进度失败:', error);
    }
  }, []);

  // 智能状态更新：只在真正需要时才更新
  const smartUpdateTasks = useCallback((newTasks) => {
    setTasks(prevTasks => {
      // 如果任务数量不同，直接更新
      if (prevTasks.length !== newTasks.length) {
        return newTasks;
      }
      
      // 检查是否有任务状态或进度发生变化
      const hasChanges = newTasks.some((newTask, index) => {
        const oldTask = prevTasks[index];
        if (!oldTask) return true;
        
        return (
          newTask.status !== oldTask.status ||
          newTask.progress !== oldTask.progress ||
          newTask.uploaded_bytes !== oldTask.uploaded_bytes ||
          newTask.error_message !== oldTask.error_message
        );
      });
      
      return hasChanges ? newTasks : prevTasks;
    });
  }, []);

  // 刷新任务列表
  const refreshTasks = async () => {
    try {
      setRefreshing(true);
      // 清理缓存并强制刷新
      progressCache.current.clear();
      await fetchTasks();
    } finally {
      setRefreshing(false);
    }
  };

  // 初始化任务列表（避免强制刷新导致的页面闪烁）
  const initializeTasks = async () => {
    try {
      setLoading(true);
      // 清理所有缓存
      progressCache.current.clear();
      // 直接获取任务列表，不清空现有列表
      await fetchTasks(true);
    } catch (error) {
      console.error('初始化任务列表失败:', error);
      message.error(t('common.getUploadTasksFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 取消上传任务
  const cancelTask = async (taskId) => {
    try {
      await axios.post(`/api/upload-tasks/${taskId}/cancel/`);
              message.success(t('common.cancelUploadTaskSuccess'));
      
      // 立即从本地状态中移除任务
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      
      // 清理进度缓存
      progressCache.current.delete(taskId);
      
      // 重新获取任务列表以确保数据同步
      await fetchTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
              message.error(t('common.cancelUploadTaskFailed'));
    }
  };

  // 删除已完成的任务
  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`/api/upload-tasks/${taskId}/`);
              message.success(t('common.deleteUploadTaskSuccess'));
      
      // 立即从本地状态中移除任务
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      
      // 清理进度缓存
      progressCache.current.delete(taskId);
      
      // 重新获取任务列表以确保数据同步
      await fetchTasks();
    } catch (error) {
      console.error('删除任务失败:', error);
              message.error(t('common.deleteUploadTaskFailed'));
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 获取状态图标和颜色
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { icon: <ClockCircleOutlined />, color: 'default', text: t('common.uploadTaskStatus.pending') };
      case 'uploading':
        return { icon: <UploadOutlined />, color: 'processing', text: t('common.uploadTaskStatus.uploading') };
      case 'success':
              return { icon: <CheckCircleOutlined />, color: 'success', text: t('common.uploadTaskStatus.completed') };
    case 'failed':
      return { icon: <CloseCircleOutlined />, color: 'error', text: t('common.uploadTaskStatus.failed') };
    case 'cancelled':
      return { icon: <PauseCircleOutlined />, color: 'warning', text: t('common.uploadTaskStatus.cancelled') };
      default:
        return { icon: <FileOutlined />, color: 'default', text: t('common.unknown') };
    }
  };

  // 获取存储方式标签
  const getStorageTag = (useOss) => {
    return useOss ? (
      <Tag icon={<CloudUploadOutlined />} color="blue">{t('common.cloudStorage')}</Tag>
    ) : (
      <Tag icon={<FileOutlined />} color="green">{t('common.localStorage')}</Tag>
    );
  };

  // 渲染任务项
  const renderTaskItem = (task) => {
    const statusInfo = getStatusInfo(task.status);
    const isActive = task.status === 'uploading' || task.status === 'pending';
    const isCompleted = task.status === 'success';
    const isFailed = task.status === 'failed' || task.status === 'cancelled';

    return (
      <List.Item
        key={task.id}
        style={{ 
          padding: '12px 0',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: '8px'
        }}
        actions={[
          isActive && (
            <Popconfirm
              title={t('common.confirmCancelUploadTask')}
              onConfirm={() => cancelTask(task.id)}
              okText={t('common.confirm')}
              cancelText={t('common.cancel')}
            >
              <Button size="small" danger icon={<CloseCircleOutlined />}>
                {t('common.cancel')}
              </Button>
            </Popconfirm>
          ),
          (isCompleted || isFailed) && (
            <Popconfirm
              title={t('common.confirmDeleteUploadTask')}
              onConfirm={() => deleteTask(task.id)}
              okText={t('common.confirm')}
              cancelText={t('common.cancel')}
            >
              <Button size="small" icon={<DeleteOutlined />}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
          )
        ].filter(Boolean)}
      >
        <List.Item.Meta
          avatar={
            <Badge status={statusInfo.color} dot>
              {statusInfo.icon}
            </Badge>
          }
          title={
            <div style={{ marginBottom: '8px' }}>
              <div style={{ 
                fontWeight: 'bold', 
                fontSize: '14px',
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {task.filename}
              </div>
              <Space size="small">
                {getStorageTag(task.use_oss)}
                <Tag color={statusInfo.color} size="small">{statusInfo.text}</Tag>
              </Space>
            </div>
          }
          description={
            <div style={{ fontSize: '12px' }}>
              <div style={{ marginBottom: '6px' }}>
                <Text type="secondary">{t('common.fileSize')}: {formatFileSize(task.file_size)}</Text>
                {(task.recent_upload_speed > 0 || task.upload_speed > 0) && (
                  <Text type="secondary" style={{ marginLeft: '8px' }}>
                    {t('common.uploadSpeed')}: {task.recent_upload_speed > 0 ? task.recent_upload_speed : task.upload_speed} MB/s
                  </Text>
                )}
              </div>
              
              <TaskProgress task={task} />
              
              {isFailed && task.error_message && (
                <Alert
                  message={t('common.uploadError')}
                  description={task.error_message}
                  type="error"
                  showIcon
                  size="small"
                  style={{ marginBottom: '6px' }}
                />
              )}
              
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {new Date(task.created_at).toLocaleString()}
              </Text>
            </div>
          }
        />
      </List.Item>
    );
  };

  // 初始化
  useEffect(() => {
    if (visible) {
      // 初始化任务列表（避免强制刷新）
      initializeTasks();
      
      // 优化后的定时器逻辑 - 智能更新
      const startRefresh = () => {
        // 状态更新：每8秒检查一次状态变化（提高频率）
        intervalRef.current = setInterval(() => {
          // 只在有活动任务时才进行状态检查
          if (hasActiveTasksRef.current) {
            // 检查距离上次更新是否超过5秒，提高更新频率
            const now = Date.now();
            if (now - lastUpdateTime.current > 5000) {
              setTimeout(() => fetchTasks(false), 0); // 不强制刷新
            }
          } else {
            // 没有活动任务时停止定时器
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        }, 8000); // 减少到8秒，提高API调用频率
        
        // 进度更新：智能频率更新（有活动任务时更频繁）
        progressIntervalRef.current = setInterval(() => {
          // 只在有活动任务时才进行进度更新
          if (hasActiveTasksRef.current) {
            // 检查是否有正在上传的任务，如果有则更频繁更新
            const hasUploadingTasks = tasks.some(task => 
              task.status === 'uploading' || task.status === 'pending'
            );
            
            // 如果有正在上传的任务，按合理频率更新；否则按正常频率更新
            if (hasUploadingTasks) {
              // 检查距离上次更新是否超过1秒，避免过于频繁的API调用
              const now = Date.now();
              if (now - lastUpdateTime.current > 1000) {
                setTimeout(() => updateProgressOnly(), 0);
              }
            } else {
              // 检查距离上次更新是否超过3秒
              const now = Date.now();
              if (now - lastUpdateTime.current > 3000) {
                setTimeout(() => updateProgressOnly(), 0);
              }
            }
          } else {
            // 没有活动任务时停止定时器
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
          }
        }, 2000); // 改为2秒，减少API调用频率
      };
      
      startRefresh();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        // 清理进度缓存
        progressCache.current.clear();
      };
    } else {
      // 当抽屉关闭时，清理所有缓存和状态
      progressCache.current.clear();
      setTasks([]);
    }
  }, [visible, updateProgressOnly]);

  // 移除重复的任务完成监听，已在fetchTasks中处理

  // 使用useMemo缓存任务列表，避免不必要的重新渲染
  const memoizedTasks = useMemo(() => tasks, [tasks]);

  // 使用useMemo缓存渲染函数，避免重复创建
  const memoizedRenderTaskItem = useCallback(renderTaskItem, []);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('common.uploadTaskList')}</span>
          <Button
            type="text"
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={refreshTasks}
            loading={refreshing}
            size="small"
            title={t('common.refresh')}
          />
        </div>
      }
      open={visible}
      onClose={onClose}
      placement="right"
      width={420}
      destroyOnClose
      zIndex={900}
      maskStyle={{ top: 64 }}
      styles={{
        header: {
          borderBottom: '1px solid #f0f0f0',
          padding: '16px 24px'
        },
        body: {
          padding: '16px 24px'
        }
      }}
    >
      <div style={{ height: 'calc(100vh - 180px)', overflow: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#666' }}>{t('common.loading')}</div>
          </div>
        ) : tasks.length === 0 ? (
          <Empty
            description={t('common.uploadTaskListEmpty')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginTop: '60px' }}
          />
        ) : (
          <List
            dataSource={memoizedTasks}
            renderItem={memoizedRenderTaskItem}
            split={false}
            size="small"
            style={{ padding: 0 }}
          />
        )}
      </div>
      
      {tasks.length > 0 && (
        <div style={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          padding: '12px 24px'
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('common.totalTasks', { count: tasks.length })}
            {tasks.filter(t => t.status === 'uploading').length > 0 && (
              <span style={{ color: '#1890ff', marginLeft: '8px' }}>
                {t('common.uploadingTasks', { count: tasks.filter(t => t.status === 'uploading').length })}
              </span>
            )}
          </Text>
        </div>
      )}
    </Drawer>
  );
};

export default UploadTaskList; 